import { useFlag } from '@afterpaytouch/feature'
import { parseCookies } from 'nookies'
import { OnfilePaymentMethod } from '@afterpaytouch/portal-api/consumer/checkout/types'
import { ApplePayEntryData, ApplePaylaterInitialState, CheckoutAppSelector, useAppSelector, useCheckoutState } from '..'
import { APPLE_ORDER_SOURCES, Features } from '../../model'
import { FLOW_COOKIE_NAME, FlowCookieValue } from '../../utils/server/flowCookie'
import { isNullOrUndefined } from '@afterpay/utils'

export const applePaylaterSelector: CheckoutAppSelector<ApplePaylaterInitialState> = (state) => state.applePaylater

export const useApplePayEntryData = (): ApplePayEntryData | undefined => {
  const applePayEntryData = useAppSelector(applePaylaterSelector)
  return applePayEntryData.entryData
}

export const useIsApplePaylaterEnabled = (): boolean => {
  return useFlag(Features.APPLE_PAYLATER_INTEGRATION)
}

export const useIsApplePaylater = (): boolean => {
  const { checkout } = useCheckoutState()

  const cookies = parseCookies()
  const flowCookie = cookies[FLOW_COOKIE_NAME] as FlowCookieValue | undefined
  const fromCookie = flowCookie === FlowCookieValue.ApplePaylater

  const isApplePaylaterMerchant = checkout?.merchant?.source ? APPLE_ORDER_SOURCES.includes(checkout.merchant.source) : false

  return fromCookie && isApplePaylaterMerchant
}

export const useIsApplePaylaterWithApplePayRepayment = (): boolean => {
  const isApplePaylater = useIsApplePaylater()
  const isAppleRepaymentEnabled = useFlag<boolean>(Features.ENABLE_APPLE_PAYLATER_APPLE_PAY_REPAYMENT, false)
  return isAppleRepaymentEnabled && isApplePaylater
}

export const useUserHasApplePaylaterCard = (): boolean | undefined => {
  const applePayState = useAppSelector(applePaylaterSelector)
  return applePayState.userHasApplePaylaterCard
}

export const useUserHasInstoreCard = (): boolean | undefined => {
  const applePayState = useAppSelector(applePaylaterSelector)
  return applePayState.userHasInstoreCard
}

export const useCardOwnerConsumerId = (): string | undefined => {
  const applePayState = useAppSelector(applePaylaterSelector)
  return applePayState.cardOwnerConsumerId
}

export const useApplePaylaterApplePay = (): OnfilePaymentMethod | null | undefined => {
  const { started } = useCheckoutState()
  return started?.onfilePaymentMethod
}

export const useApplePaylaterApplePayDisplay = (): { lastFour: string | null | undefined; expiryDate: string | undefined } => {
  const data = useApplePaylaterApplePay()
  const { lastFour, expiryMonth, expiryYear } = data ?? {}

  const formatExpiryDate = (month: string | null | undefined, year: string | null | undefined): string | undefined => {
    if (isNullOrUndefined(month) || isNullOrUndefined(year)) {
      return undefined
    }

    const monthTrimmed = month.trim()
    const yearTrimmed = year.trim()

    // Month must be 1 or 2 digits and within the 1-12 range
    if (!/^\d{1,2}$/.test(monthTrimmed)) {
      return undefined
    }

    const monthNumber = Number(monthTrimmed)
    if (!Number.isInteger(monthNumber) || monthNumber < 1 || monthNumber > 12) {
      return undefined
    }

    // Year must be a non-empty string of digits
    if (!/^\d+$/.test(yearTrimmed)) {
      return undefined
    }

    const paddedMonth = monthTrimmed.padStart(2, '0')
    const shortYear = yearTrimmed.padStart(2, '0').slice(-2)

    return `${paddedMonth}/${shortYear}`
  }

  const expiryDate = formatExpiryDate(expiryMonth, expiryYear)
  return { lastFour, expiryDate }
}
